/* eslint-disable indent */
import { apiPaths, getApiPath, httpStatus } from '../../constants'
import { Api } from '../../middlewares'
import { Logger, LoggerTypes } from '../../utils'

// -- GET (list object)
export const GET_ACCOUNTS_SUCCESS = 'GET_ACCOUNTS_SUCCESS'
export const GET_ACCOUNTS_FAILURE = 'GET_ACCOUNTS_FAILURE'

// -- GET (one object)
export const GET_ACCOUNT_SUCCESS = 'GET_ACCOUNT_SUCCESS'
export const GET_ACCOUNT_FAILURE = 'GET_ACCOUNT_FAILURE'

// -- GET (one object)
export const GET_CURRENT_ACCOUNT_SUCCESS = 'GET_CURRENT_ACCOUNT_SUCCESS'
export const GET_CURRENT_ACCOUNT_FAILURE = 'GET_CURRENT_ACCOUNT_FAILURE'

// -- POST
export const CREATE_ACCOUNT_SUCCESS = 'CREATE_ACCOUNT_SUCCESS'
export const CREATE_ACCOUNT_FAILURE = 'CREATE_ACCOUNT_FAILURE'

// -- PUT
export const UPDATE_ACCOUNT_SUCCESS = 'UPDATE_ACCOUNT_SUCCESS'
export const UPDATE_ACCOUNT_FAILURE = 'UPDATE_ACCOUNT_FAILURE'

// -- DELETE
export const DELETE_ACCOUNT_SUCCESS = 'DELETE_ACCOUNT_SUCCESS'
export const DELETE_ACCOUNT_FAILURE = 'DELETE_ACCOUNT_FAILURE'

// -- GET (one object)
export const GET_ACCOUNT_RIGHTS_SUCCESS = 'GET_ACCOUNT_RIGHTS_SUCCESS'
export const GET_ACCOUNT_RIGHTS_FAILURE = 'GET_ACCOUNT_RIGHTS_FAILURE'

// ____________________________________________________________

/**
 * Get the list of all accounts and broadcast two types of events:
 * - GET_ACCOUNTS_SUCCESS: in case of success
 * - GET_ACCOUNTS_FAILURE: in case of failure.
 *
 * @returns {Promise<object>}
 */
export const getAccounts = () => async (dispatch) => {
  const path = apiPaths.ACCOUNT_LIST
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] accountsActions.getAccounts()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    const data = result.data

    return dispatch({
      type: GET_ACCOUNTS_SUCCESS,
      data: data.items
    })
  }
  return dispatch({
    type: GET_ACCOUNTS_FAILURE,
    data: result.data
  })
}

/**
 * Get a specific account and broadcast two types of events:
 * - GET_ACCOUNT_SUCCESS: in case of success
 * - GET_ACCOUNT_FAILURE: in case of failure.
 *
 * @param {uuid} uid account's ID
 * @returns {Promise<object>}
 */
export const getAccount = (uid) => async (dispatch) => {
  const path = getApiPath(apiPaths.ACCOUNT_GET, { uid })
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] accountsActions.getAccount()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: GET_ACCOUNT_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: GET_ACCOUNT_FAILURE,
    data: result.data
  })
}

/**
 * Get a specific account and broadcast two types of events:
 * - GET_CURRENT_ACCOUNT_SUCCESS: in case of success
 * - GET_CURRENT_ACCOUNT_FAILURE: in case of failure.
 *
 * @returns {Promise<object>}
 */
export const getCurrentAccount = () => async (dispatch) => {
  Logger.trace(LoggerTypes.ACTION, '[ACTION] accountsActions.getCurrentAccount()', {
    api: apiPaths.ACCOUNT_CURRENT_GET
  })
  const path = getApiPath(apiPaths.ACCOUNT_CURRENT_GET, {})
  Logger.trace(LoggerTypes.ACTION, '[ACTION] accountsActions.getCurrentAccount()', {
    api: apiPaths.ACCOUNT_CURRENT_GET,
    path
  })
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] accountsActions.getCurrentAccount()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: GET_CURRENT_ACCOUNT_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: GET_CURRENT_ACCOUNT_FAILURE,
    data: result.data
  })
}

/**
 * Create a specific account and broadcast two types of events:
 * - CREATE_ACCOUNT_SUCCESS: in case of success.
 * - CREATE_ACCOUNT_FAILURE: in case of failure.
 *
 * @param {object} account account's object
 * @returns {Promise<object>}
 */
export const createAccount = (account) => async (dispatch) => {
  const path = apiPaths.ACCOUNT_CREATE
  const api = new Api(path)
  const result = await api.create(account)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] accountsActions.createAccount()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: CREATE_ACCOUNT_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: CREATE_ACCOUNT_FAILURE,
    data: result.data
  })
}

/**
 * Update a specific account and broadcast two types of events:
 * - UPDATE_ACCOUNT_SUCCESS: in case of success.
 * - UPDATE_ACCOUNT_FAILURE: in case of failure.
 *
 * @param {object} account account's object
 * @returns {Promise<object>}
 */
export const updateAccount = (account) => async (dispatch) => {
  const path = getApiPath(apiPaths.ACCOUNT_UPDATE, { uid: account.id })
  const api = new Api(path)
  const result = await api.update(account)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] accountsActions.updateAccount()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: UPDATE_ACCOUNT_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: UPDATE_ACCOUNT_FAILURE,
    data: result.data
  })
}

/**
 * Update a specific account and broadcast two types of events:
 * - DELETE_ACCOUNT_SUCCESS: in case of success.
 * - DELETE_ACCOUNT_FAILURE: in case of failure.
 *
 * @param {number} accountId account's ID
 * @returns {Promise<object>}
 */
export const deleteAccount = (accountId) => async (dispatch) => {
  const path = getApiPath(apiPaths.ACCOUNT_DELETE, { uid: accountId })
  const api = new Api(path)
  const result = await api.delete()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] accountsActions.deleteAccount()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: DELETE_ACCOUNT_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: DELETE_ACCOUNT_FAILURE,
    data: result.data
  })
}

/**
 * Get a specific account rights and broadcast two types of events:
 * - GET_ACCOUNT_RIGHTS_SUCCESS: in case of success
 * - GET_ACCOUNT_RIGHTS_FAILURE: in case of failure.
 *
 * @param {bool} isCache define if we have to store the data retrieve in local cache.
 * @param {UUID} accountID account's ID of account rights to retrieve.
 * @returns {Promise<object>}
 */
export const getAccountRights = () => async (dispatch) => {
  const path = apiPaths.ACCOUNT_RIGHTS_GET
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] accountsActions.getAccountRights()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: GET_ACCOUNT_RIGHTS_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: GET_ACCOUNT_RIGHTS_FAILURE,
    data: result.data
  })
}
